/**
 * Pre-built dashboard templates for quick setup.
 */

import type { DashboardTemplate, DashboardWidget } from '@/types/dashboard'

/** Helper to create a widget definition without an ID (templates don't have IDs yet) */
function w(
  type: DashboardWidget['type'],
  title: string,
  layout: DashboardWidget['layout'],
  config: DashboardWidget['config'] = {},
): Omit<DashboardWidget, 'id'> {
  return { type, title, layout, config }
}

export const DASHBOARD_TEMPLATES: DashboardTemplate[] = [
  {
    id: 'overview',
    name: 'Overview',
    description: 'High-level KPIs, attribution funnel, active campaigns, and optimization actions',
    icon: 'LayoutDashboard',
    globalDateRange: 30,
    widgets: [
      w('kpi_card', 'Total Spend', { x: 0, y: 0, w: 3, h: 2 }, { kpiMetric: 'totalSpend' }),
      w('kpi_card', 'Impressions', { x: 3, y: 0, w: 3, h: 2 }, { kpiMetric: 'totalImpressions' }),
      w('kpi_card', 'Clicks', { x: 6, y: 0, w: 3, h: 2 }, { kpiMetric: 'totalClicks' }),
      w('kpi_card', 'Conversations', { x: 9, y: 0, w: 3, h: 2 }, { kpiMetric: 'totalConversations' }),
      w('attribution_funnel', 'Attribution Funnel', { x: 0, y: 2, w: 12, h: 4 }),
      w('active_campaigns_list', 'Active Campaigns', { x: 0, y: 6, w: 6, h: 4 }),
      w('optimization_actions', 'Optimization Actions', { x: 6, y: 6, w: 6, h: 4 }),
    ],
  },
  {
    id: 'campaign_deep_dive',
    name: 'Campaign Deep-Dive',
    description: 'Detailed campaign analytics with trends, comparisons, and health scores',
    icon: 'Search',
    globalDateRange: 14,
    widgets: [
      w('performance_trends', 'Performance Trends', { x: 0, y: 0, w: 12, h: 4 }, { metrics: ['spend', 'clicks', 'conversations'], enableArea: true }),
      w('campaign_comparison', 'Campaign Comparison', { x: 0, y: 4, w: 6, h: 4 }, { metrics: ['spend', 'leads'] }),
      w('campaign_health_radar', 'Campaign Health', { x: 6, y: 4, w: 6, h: 5 }),
      w('campaign_performance_table', 'Performance Table', { x: 0, y: 9, w: 12, h: 5 }),
      w('budget_sankey', 'Budget Flow', { x: 0, y: 14, w: 6, h: 4 }),
      w('spend_roas_scatter', 'Spend vs. ROAS', { x: 6, y: 14, w: 6, h: 4 }),
    ],
  },
  {
    id: 'conversion_focus',
    name: 'Conversion Focus',
    description: 'Conversion pipeline, journey tracking, and ROAS analysis',
    icon: 'Target',
    globalDateRange: 30,
    widgets: [
      w('kpi_card', 'Leads', { x: 0, y: 0, w: 3, h: 2 }, { kpiMetric: 'totalLeads' }),
      w('kpi_card', 'Purchases', { x: 3, y: 0, w: 3, h: 2 }, { kpiMetric: 'totalPurchases' }),
      w('kpi_card', 'Avg CPC', { x: 6, y: 0, w: 3, h: 2 }, { kpiMetric: 'avgCpc' }),
      w('kpi_card', 'Active Campaigns', { x: 9, y: 0, w: 3, h: 2 }, { kpiMetric: 'activeCampaigns' }),
      w('attribution_funnel', 'Conversion Funnel', { x: 0, y: 2, w: 12, h: 4 }),
      w('spend_roas_scatter', 'Spend vs. ROAS', { x: 0, y: 6, w: 6, h: 5 }),
      w('journey_timeline', 'Customer Journeys', { x: 6, y: 6, w: 6, h: 3 }),
      w('optimization_actions', 'Recommended Actions', { x: 6, y: 9, w: 6, h: 4 }),
    ],
  },
  {
    id: 'executive_summary',
    name: 'Executive Summary',
    description: 'Clean, high-level view for stakeholders and leadership',
    icon: 'Briefcase',
    globalDateRange: 30,
    widgets: [
      w('kpi_card', 'Total Spend', { x: 0, y: 0, w: 3, h: 2 }, { kpiMetric: 'totalSpend' }),
      w('kpi_card', 'Conversations', { x: 3, y: 0, w: 3, h: 2 }, { kpiMetric: 'totalConversations' }),
      w('kpi_card', 'Leads', { x: 6, y: 0, w: 3, h: 2 }, { kpiMetric: 'totalLeads' }),
      w('kpi_card', 'Purchases', { x: 9, y: 0, w: 3, h: 2 }, { kpiMetric: 'totalPurchases' }),
      w('performance_trends', 'Trends', { x: 0, y: 2, w: 12, h: 4 }, { metrics: ['spend', 'conversations', 'leads'], enableArea: true }),
      w('budget_sankey', 'Budget Flow', { x: 0, y: 6, w: 12, h: 5 }),
      w('active_campaigns_list', 'Active Campaigns', { x: 0, y: 11, w: 6, h: 4 }),
      w('campaign_health_radar', 'Campaign Health', { x: 6, y: 11, w: 6, h: 5 }),
    ],
  },
]

/** Get a template by ID */
export function getTemplate(id: string): DashboardTemplate | undefined {
  return DASHBOARD_TEMPLATES.find((t) => t.id === id)
}
